import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Usługi", to: "/services", id: "services" },
  { label: "Portfolio", to: "/portfolio", id: "portfolio" },
  { label: "O nas", to: "/about", id: "about" },
  { label: "Kontakt", to: "/contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-[100] flex justify-center px-4 pt-4"
      data-testid="main-navbar"
    >
      <nav className={`w-full max-w-7xl flex items-center justify-between rounded-full px-5 md:px-7 py-3 transition-colors duration-500 ${scrolled ? "glass-dark max-md:!bg-[#0a0a0a]/95" : "bg-transparent border border-transparent"}`}>
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-60 group-hover:animate-ping" style={{ background: "var(--gold)" }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "var(--gold)" }} />
          </span>
          <span className="text-lg font-semibold tracking-tight text-white">VELIORA</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={`text-sm tracking-tight transition-colors duration-300 ${pathname === l.to ? "text-white" : "text-white/55 hover:text-white"}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center rounded-full bg-white text-black text-sm font-medium px-5 py-2.5 transition-transform duration-300 hover:-translate-y-0.5"
          >
            Umów konsultację
          </button>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="md:hidden absolute top-20 left-4 right-4 glass-dark rounded-3xl p-6 flex flex-col gap-4"
          >
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="text-white/80 text-lg" data-testid={`mobile-nav-${l.id}`}>{l.label}</Link>
            ))}
            <button onClick={() => navigate("/contact")} className="mt-2 rounded-full bg-white text-black py-3 font-medium">Umów konsultację</button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
